import { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Calendar } from 'primereact/calendar';
import styles from './Stoffstrombilanzen.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../configureStore';
import './prime-react-theme/theme.css';
import Select from 'react-select';
import { CSSObject } from '@emotion/serialize';
import {
    checkHasStoffstrombilanzen,
    checkInfobaseAvailable,
    deleteStoffstrombilanz,
    downloadError,
    downloadSuccess,
    loadStoffstrombilanzen,
    resetIsLoaded,
    sendStoffstrombilanzRequest,
    setEndDate,
    setStartDate,
    setTimePeriodType,
    setWePartners,
    Stoffstrombilanz,
    STOFFSTROMBILANZ_SORT_OPTIONS,
    StoffstrombilanzFile,
    TimePeriod,
} from '../../store/Stoffstrombilanz.store';
import { getWarenempfaenger } from '../../store/Warenempfaenger.store';

import csvDownloadIcon from '../../assets/images/csvDownload.png';
import pdfDownloadIcon from '../../assets/images/pdfDownload.png';
import xlsDownloadIcon from '../../assets/images/xlsDownload.png';
import { TableInstance, usePagination, useSortBy, useTable } from 'react-table';
import { StoffstrombilanzOverviewTable } from '../../shared/ui-components/OverviewTable/OverviewTable';
import { OverviewPagination } from '../../shared/ui-components/OverviewPagination/OverviewPagination';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import { postData } from '../../shared/fetchApi';
import Config from '../../shared/config';
import FlutterBridge from '../../flutter/flutterBridge';
import NotificationBar from '../../shared/ui-components/NotificationBar/NotificationBar';
import settingsIcon from '../../assets/settings.svg';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';
import { selectCurrentUser } from '../../store/keycloak.store';

function getTabClass(tabName: string, selectedTab: string) {
    const tabClass = 'nav-item';
    return tabName === selectedTab ? tabClass + ' ' + styles.activeTab : tabClass;
}

export const Stoffstrombilanzen = (): JSX.Element => {
    const [selectedTab, setSelectedTab] = useState<string | undefined>(undefined);
    const [showNeueAnfrageDetails] = useState(false);
    const dispatch = useDispatch();
    const isLoading = useSelector((root: RootState) => root.stoffstrombilanz.isLoading);
    const infobaseAvailable = useSelector((root: RootState) => root.stoffstrombilanz.infobaseAvailable);
    const hasStoffstrombilanzen = useSelector((root: RootState) => root.stoffstrombilanz.hasStoffstrombilanzen);
    const isCheckingHasStoffstrombilanzen = useSelector((root: RootState) => root.stoffstrombilanz.isCheckingHasStoffstrombilanzen);
    const history = useHistory();

    useEffect(() => {
        dispatch(checkHasStoffstrombilanzen());
        dispatch(getWarenempfaenger());
        dispatch(checkInfobaseAvailable());
    }, [dispatch]);

    useEffect(() => {
        console.log('useEffect triggered - hasStoffstrombilanzen changed to:', hasStoffstrombilanzen);
        if (hasStoffstrombilanzen !== undefined) {
            const newTab = hasStoffstrombilanzen ? 'MEINE BILANZEN' : 'NEUE ANFRAGE';
            console.log('Setting selectedTab to:', newTab);
            setSelectedTab(newTab);
        }
    }, [hasStoffstrombilanzen]);

    // const handleSettingsClick = () => {
    //     history.push('/stoffstrombilanzen/einstellungen');
    // };

    // Previously showed an unavailability message when infobaseAvailable === false.
    // Requirement: Never display that message; continue rendering the page without it.

    console.log('Render state:', {
        selectedTab,
        hasStoffstrombilanzen,
        isCheckingHasStoffstrombilanzen,
        shouldShowLoading: selectedTab === undefined || isCheckingHasStoffstrombilanzen,
    });

    // Show loading spinner while checking if user has stoffstrombilanzen
    if (selectedTab === undefined || isCheckingHasStoffstrombilanzen) {
        return (
            <div style={{ position: 'relative', minHeight: '100vh' }}>
                <LoadingSpinner isLoading={true}>
                    <></>
                </LoadingSpinner>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', minHeight: '100vh' }}>
            <div className="mx-auto">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <h1 className="h3 fw-bold mb-4" style={{ marginBottom: 0 }}>
                        MEINE STOFFSTROMBILANZEN
                    </h1>
                    {/* 
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            marginLeft: 16,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        onClick={handleSettingsClick}
                        aria-label="Einstellungen"
                    >
                        <img src={settingsIcon} alt="Einstellungen" style={{ width: 28, height: 28, marginRight: 8 }} />
                        <span style={{ fontSize: 16, color: '#444', fontWeight: 400, marginTop: 8, display: 'inline-block' }}>
                            Einstellungen
                        </span>
                    </button>
                    */}
                </div>
                <ul className="nav mb-4" id="tabs" role="tablist">
                    <li
                        className={getTabClass('NEUE ANFRAGE', selectedTab)}
                        role="presentation"
                        onClick={() => setSelectedTab('NEUE ANFRAGE')}
                    >
                        <button
                            className={'nav-link ' + styles.stoffstrombilanzTab}
                            id="new-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#new"
                            type="button"
                            role="tab"
                        >
                            NEUE ANFRAGE
                        </button>
                    </li>
                    <li
                        className={getTabClass('MEINE BILANZEN', selectedTab)}
                        role="presentation"
                        onClick={() => setSelectedTab('MEINE BILANZEN')}
                    >
                        <button className={'nav-link ' + styles.stoffstrombilanzTab} id="existing-tab" type="button" role="tab">
                            MEINE BILANZEN
                        </button>
                    </li>
                </ul>

                <div className="tab-content" id="tabsContent">
                    <div className="tab-pane fade show active" id="new" role="tabpanel"></div>
                </div>
                {selectedTab === 'NEUE ANFRAGE' && (
                    <LoadingSpinner
                        isLoading={isLoading}
                        loadingMessage={
                            <>
                                Die Stoffstrombilanz wird im Hintergrund erstellt.
                                <br />
                                Sie finden sie anschließend unter „Meine Bilanzen“.
                            </>
                        }
                    >
                        {/* Always render the form, regardless of Infobase availability */}
                        <>
                            <NeueAnfrage />
                            {showNeueAnfrageDetails && (
                                <div style={{ padding: 40, background: '#f9f9f9', borderRadius: 8, marginTop: 24 }}>
                                    <h2>Details zu NEUE ANFRAGE</h2>
                                    <div>Hier können weitere Informationen oder Einstellungen angezeigt werden.</div>
                                </div>
                            )}
                        </>
                    </LoadingSpinner>
                )}

                {selectedTab === 'MEINE BILANZEN' && (
                    <>
                        <StoffstrombilanzUebersicht />
                    </>
                )}
            </div>
        </div>
    );
};

const PORTAL_RECIPIENT = 'PORTAL_RECIPIENT';

function NeueAnfrage() {
    const dispatch = useDispatch();
    const isLoading = useSelector((state: RootState) => state.stoffstrombilanz.isLoading);
    const isLoaded = useSelector((state: RootState) => state.stoffstrombilanz.isLoaded);
    const stoffstrombilanzRequest = useSelector((state: RootState) => state.stoffstrombilanz.request);
    const stoffstrombilanzResponse = useSelector((state: RootState) => state.stoffstrombilanz.response);
    const warenempfaengerStore = useSelector((state: RootState) => state.warenempfaenger.warenempfaenger);
    const currentUser = useSelector(selectCurrentUser);
    const warenempfaengerOptions = warenempfaengerStore.map((item) => ({ value: item, label: item.hauptname + ' ' + item.partnerNummer }));
    const createStoffstrombilanzError = useSelector((state: RootState) => state.stoffstrombilanz.isNewStoffstrombilanzError);
    const errorMessage = useSelector((state: RootState) => state.stoffstrombilanz.errorMessage);
    const isMobile = window.isMobile;

    return (
        <>
            <NotificationBar message={errorMessage || ''} isVisible={createStoffstrombilanzError || false} />
            <form style={isMobile ? { minWidth: '100%' } : { maxWidth: '75%' }}>
                <div className="mb-4">
                    <label className="form-label fw-semibold">Zeitraum wählen</label>
                    <div className="d-flex flex-wrap gap-3">
                        <div className="form-check" onClick={() => setTimePeriodType(TimePeriod.FINANCIAL_YEAR)}>
                            <input
                                className={'form-check-input ' + styles.customRadio}
                                type="radio"
                                name="periodType"
                                id="fiscalYear"
                                value={TimePeriod.FINANCIAL_YEAR}
                                checked={stoffstrombilanzRequest.timePeriod.type === TimePeriod.FINANCIAL_YEAR}
                                onClick={() => dispatch(setTimePeriodType(TimePeriod.FINANCIAL_YEAR.toString()))}
                                disabled={isLoading || isLoaded}
                            />
                            <label
                                className="form-check-label"
                                style={{
                                    verticalAlign: 'sub',
                                }}
                                htmlFor="fiscalYear"
                            >
                                Wirtschaftsjahr
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className={'form-check-input ' + styles.customRadio}
                                type="radio"
                                name="periodType"
                                id="calenderYear"
                                value={TimePeriod.CALENDAR_YEAR}
                                checked={stoffstrombilanzRequest.timePeriod.type === TimePeriod.CALENDAR_YEAR}
                                onClick={() => dispatch(setTimePeriodType(TimePeriod.CALENDAR_YEAR))}
                                disabled={isLoading || isLoaded}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="calenderYear"
                                style={{
                                    verticalAlign: 'sub',
                                }}
                            >
                                Kalenderjahr
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className={'form-check-input ' + styles.customRadio}
                                type="radio"
                                name="periodType"
                                id="custom"
                                value={TimePeriod.CUSTOM}
                                checked={stoffstrombilanzRequest.timePeriod.type === TimePeriod.CUSTOM}
                                onChange={() => dispatch(setTimePeriodType(TimePeriod.CUSTOM))}
                                disabled={isLoading || isLoaded}
                            />
                            <label
                                className="form-check-label"
                                style={{
                                    verticalAlign: 'sub',
                                }}
                                htmlFor="custom"
                            >
                                Benutzerdefiniert
                            </label>
                        </div>
                    </div>
                </div>

                {stoffstrombilanzRequest.timePeriod.type == TimePeriod.CUSTOM && (
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label htmlFor="dateFrom" className="form-label">
                                Von
                            </label>
                            <br />
                            <Calendar
                                value={stoffstrombilanzRequest.timePeriod.periodBegin}
                                onChange={(e) => dispatch(setStartDate(e.target.value || new Date()))}
                                ariaLabel={'test'}
                                readOnlyInput={true}
                                disabled={isLoading || isLoaded}
                                view="date"
                                dateFormat="dd/mm/yy"
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                showIcon={true}
                                style={{ minWidth: '100%', color: 'black' }}
                            />
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="dateTo" className="form-label">
                                Bis
                            </label>
                            <br />
                            <Calendar
                                value={stoffstrombilanzRequest.timePeriod.periodEnd}
                                onChange={(e) => {
                                    console.log(e.target.value);
                                    dispatch(setEndDate(e.target.value || new Date()));
                                }}
                                ariaLabel={'test'}
                                disabled={isLoading || isLoaded}
                                readOnlyInput={true}
                                view="date"
                                dateFormat="dd/mm/yy"
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                showIcon={true}
                                style={{ minWidth: '100%', color: 'black' }}
                            />
                        </div>
                    </div>
                )}

                {stoffstrombilanzRequest.timePeriod.type == TimePeriod.CALENDAR_YEAR && (
                    <div className={'row mb-4'}>
                        <label htmlFor="dateFrom" className="form-label">
                            Kalenderjahr
                        </label>
                        <Calendar
                            value={stoffstrombilanzRequest.timePeriod.periodBegin}
                            onChange={(e) => dispatch(setStartDate(e.target.value || new Date()))}
                            readOnlyInput={true}
                            disabled={isLoading || isLoaded}
                            view="year"
                            dateFormat="yy"
                            minDate={new Date(2020, 0, 1)}
                            maxDate={new Date()}
                            showIcon={true}
                            style={{ minWidth: '100%', color: 'black' }}
                        />
                    </div>
                )}

                {stoffstrombilanzRequest.timePeriod.type == TimePeriod.FINANCIAL_YEAR && (
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label htmlFor="dateFrom" className="form-label">
                                Von
                            </label>
                            <br />
                            <Calendar
                                value={stoffstrombilanzRequest.timePeriod.periodBegin}
                                readOnlyInput={true}
                                disabled={isLoading || isLoaded}
                                onChange={(e) => dispatch(setStartDate(e.target.value || new Date()))}
                                ariaLabel={'test'}
                                view="year"
                                dateFormat="dd/mm/yy"
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                showIcon={true}
                                style={{ minWidth: '100%', color: 'black' }}
                            />
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="dateTo" className="form-label">
                                Bis
                            </label>
                            <br />
                            <Calendar
                                value={stoffstrombilanzRequest.timePeriod.periodEnd}
                                readOnlyInput={true}
                                disabled={true}
                                ariaLabel={'test'}
                                view="year"
                                dateFormat="dd/mm/yy"
                                minDate={new Date(2020, 0, 1)}
                                maxDate={new Date()}
                                showIcon={true}
                                style={{ minWidth: '100%', color: 'black' }}
                            />
                        </div>
                    </div>
                )}

                <div className="mb-4" style={{ width: '100%' }}>
                    <label htmlFor="receiver" className="form-label">
                        Warenempfänger (optional)
                    </label>
                    <br />
                    <Select
                        isDisabled={isLoading || isLoaded}
                        styles={{
                            control(base: CSSObject, _state): CSSObject {
                                return {
                                    ...base,
                                    backgroundColor: '#fff8d3',
                                    opacity: isLoaded || isLoading ? 0.2 : 1,
                                    borderColor: '#e0b300',
                                    ':focus': {
                                        borderColor: '#e0b300',
                                        borderRadius: '1px',
                                        boxShadow: 'none',
                                    },
                                    ':hover': {
                                        borderColor: '#e0b300',
                                        borderRadius: '1px',
                                        boxShadow: 'none',
                                    },
                                    borderRadius: '1px',
                                    color: 'black',
                                    fontSize: '1rem',
                                    height: 'auto',
                                    boxShadow: 'none',
                                };
                            },
                            option(base: CSSObject, state): CSSObject {
                                return {
                                    ...base,
                                    backgroundColor: state.isFocused || state.isSelected ? '#fd0' : 'white',
                                    color: 'black',
                                };
                            },
                        }}
                        onChange={(e) => {
                            dispatch(setWePartners(e.map((item) => item.value.partnerNummer)));
                            //provides selected options
                        }}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        isClearable={true}
                        value={warenempfaengerOptions.filter((opt) =>
                            (stoffstrombilanzRequest.wePartners || []).includes(opt.value.partnerNummer)
                        )}
                        name="warenempfaengerOptions"
                        options={warenempfaengerOptions}
                    />
                </div>

                <button
                    className="btn w-100"
                    type="button"
                    disabled={isLoading}
                    style={{ backgroundColor: '#FFD700', opacity: isLoading ? 0.35 : 1, color: 'black' }}
                    onClick={() => {
                        if (!isLoaded) {
                            const isWE = currentUser?.roles?.includes(PORTAL_RECIPIENT);
                            const wePartners =
                                (stoffstrombilanzRequest.wePartners?.length ?? 0) > 0
                                    ? stoffstrombilanzRequest.wePartners
                                    : isWE
                                    ? warenempfaengerStore.map((w) => w.partnerNummer)
                                    : [];
                            const request = {
                                ...stoffstrombilanzRequest,
                                wePartners,
                            };
                            dispatch(sendStoffstrombilanzRequest(request));
                        } else dispatch(resetIsLoaded());
                    }}
                >
                    {!isLoaded ? 'Anfordern' : 'Neu Anfordern'}
                </button>

                {isLoaded && stoffstrombilanzResponse != null && (
                    <div className={styles.downloadButtonContainer}>
                        {stoffstrombilanzResponse.files?.map((file, index) => (
                            <DownloadFile
                                key={file.fileName ? `${file.fileName}-${file.fileType}` : `file-${index}`}
                                base64={file.base64Encoding}
                                type={file.fileType}
                                icon={getDownloadIcon(file.fileType)}
                                fileName={file.fileName || ''}
                                stoffstrombilanzId={stoffstrombilanzResponse.id}
                            />
                        ))}
                    </div>
                )}
            </form>
        </>
    );
}

//TODO load count from backend
//TODO fix WE loading in backend
//TODO add routing for WE and FL
function StoffstrombilanzUebersicht() {
    const isLoading = useSelector((state: RootState) => state.stoffstrombilanz.isLoading);
    const selectedPartner = useSelector((state: RootState) => state.stoffstrombilanz.selectedPartner);

    const warenEmpfaenger = useSelector((state: RootState) => state.warenempfaenger.warenempfaenger);
    const warenempfaengerOptions = warenEmpfaenger.map((item) => ({ value: item, label: item.hauptname + ' ' + item.partnerNummer }));
    const stoffstrombilanzen = useSelector((state: RootState) => state.stoffstrombilanz.stoffstrombilanzenPage);
    const sortOption = useSelector((root: RootState) => root.stoffstrombilanz.sortBy);
    const columns = useMemo(
        () => [getCreatedAtColumn(), getWarenEmpfaengerColumn(), getStartDateColumn(), getEndDateColumn(), getFileColumn()],
        []
    );
    const data = useMemo(() => {
        return stoffstrombilanzen.stoffstrombilanzList;
    }, [stoffstrombilanzen]);
    const [pageSize, setPageSize] = useState(10);
    const dispatch = useDispatch();

    const deleteStoffstrombilanzError = useSelector((state: RootState) => state.stoffstrombilanz.isDeleteError);
    const downloadStoffstrombilanzError = useSelector((state: RootState) => state.stoffstrombilanz.isDownloadError);
    const loadStoffstrombilanzError = useSelector((state: RootState) => state.stoffstrombilanz.isLoadError);
    const errorMessage = useSelector((state: RootState) => state.stoffstrombilanz.errorMessage);

    const tableInstance: TableInstance<Stoffstrombilanz> = useTable<Stoffstrombilanz>(
        {
            columns,
            data,
            initialState: {
                sortBy: [
                    {
                        id: sortOption,
                        desc: true,
                    },
                ],
                pageSize: pageSize,
            },
            pageCount: Math.ceil(stoffstrombilanzen.totalCount / pageSize),
            manualPagination: true,
            manualSortBy: true,
            disableMultiSort: true,
            disableSortRemove: true,
        },
        useSortBy,
        usePagination
    );

    const { state } = tableInstance;

    useEffect(() => {
        dispatch(
            loadStoffstrombilanzen({
                number: state.pageIndex,
                size: state.pageSize,
                sort: state.sortBy[0].id + ',' + (state.sortBy[0].desc ? 'desc' : 'asc'),
                partnerNummer: selectedPartner,
            })
        );
    }, [dispatch, selectedPartner, state.pageIndex, state.pageSize, state.sortBy]);

    return (
        <>
            <NotificationBar
                message={errorMessage || ''}
                isVisible={downloadStoffstrombilanzError || loadStoffstrombilanzError || deleteStoffstrombilanzError || false}
            />
            <div>
                <Select
                    isClearable={true}
                    styles={{
                        control(base: CSSObject, _state): CSSObject {
                            return {
                                ...base,
                                backgroundColor: '#fff8d3',
                                borderColor: '#e0b300',
                                ':focus': {
                                    borderColor: '#e0b300',
                                    borderRadius: '1px',
                                    boxShadow: 'none',
                                },
                                ':hover': {
                                    borderColor: '#e0b300',
                                    borderRadius: '1px',
                                    boxShadow: 'none',
                                },
                                borderRadius: '1px',
                                color: 'black',
                                fontSize: '1rem',
                                height: 'auto',
                                boxShadow: 'none',
                                marginBottom: '3vh',
                                maxWidth: '50%',
                                marginLeft: 'auto',
                            };
                        },
                        option(base: CSSObject, _state): CSSObject {
                            return {
                                ...base,
                                backgroundColor: 'white',
                                ':hover': {
                                    backgroundColor: '#fd0',
                                },
                                ':focus': {
                                    backgroundColor: '#fd0',
                                },
                                color: 'black !important',
                                marginLeft: 'auto',
                            };
                        },
                    }}
                    onChange={(e) => {
                        if (e == null)
                            dispatch(
                                loadStoffstrombilanzen({
                                    number: state.pageIndex,
                                    size: state.pageSize,
                                    sort: state.sortBy[0].id + ',' + (state.sortBy[0].desc ? 'desc' : 'asc'),
                                    partnerNummer: undefined,
                                })
                            );
                        else
                            dispatch(
                                loadStoffstrombilanzen({
                                    number: state.pageIndex,
                                    size: state.pageSize,
                                    sort: state.sortBy[0].id + ',' + (state.sortBy[0].desc ? 'desc' : 'asc'),
                                    partnerNummer: e?.value.partnerNummer,
                                })
                            );
                    }}
                    name="warenempfaengerOptions"
                    options={warenempfaengerOptions}
                />
                <LoadingSpinner isLoading={isLoading}>
                    <StoffstrombilanzOverviewTable showSortingIcon={true} tableInstance={tableInstance} />
                </LoadingSpinner>
                {!isLoading && (
                    <OverviewPagination
                        tableInstance={tableInstance}
                        totalCount={stoffstrombilanzen.totalCount}
                        setPageSizeState={setPageSize}
                    />
                )}
            </div>
        </>
    );
}

function getCreatedAtColumn() {
    return {
        id: STOFFSTROMBILANZ_SORT_OPTIONS.CREATION_DATE, // Use this ID for sorting
        Header: 'Erstellungsdatum',
        accessor: (row: Stoffstrombilanz) => row.createdAt, // Raw date used for sorting
        sortType: customDateSort, // Apply custom sorting
        Cell: ({ value }: { value: Date }) => {
            return (
                <>
                    <DateElement date={new Date(value)} />
                </>
            );
        },
    };
}

const customDateSort = (
    rowA: { values: { [key: string]: unknown } },
    rowB: { values: { [key: string]: unknown } },
    columnId: string
): number => {
    const dateA = new Date(rowA.values[columnId] as string | number | Date);
    const dateB = new Date(rowB.values[columnId] as string | number | Date);

    // Check if the dates are valid
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        return 0; // Treat invalid dates as equal
    }

    // Compare the timestamps
    return dateA.getTime() - dateB.getTime();
};

const getWarenEmpfaengerColumn = () => {
    return {
        id: 'warenempfaenger',
        Header: 'Warenempfaenger',
        disableSortBy: true,
        accessor: function accessor(row: Stoffstrombilanz) {
            return (
                <div className={styles.column}>
                    {row.partners?.map((p) => {
                        return (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap', marginRight: '10px' }}>
                                    <div className={styles.columnText}>{p.partnerName}</div>
                                    <div className={styles.columnSubtext}>{p.partnerNummer}</div>
                                </div>
                            </>
                        );
                    })}
                </div>
            );
        },
    };
};

const getStartDateColumn = () => {
    return {
        id: STOFFSTROMBILANZ_SORT_OPTIONS.START_DATE,
        Header: 'Startdatum',
        accessor: (row: Stoffstrombilanz) => new Date(row.startDate), // Use raw date value for sorting
        sortType: customDateSort, // Apply custom sorting function
        Cell: ({ value }: { value: Date | null }) => {
            // Render custom component with formatting
            return value ? <StartEndDateColumn date={value} /> : '-';
        },
    };
};

const getFileColumn = () => {
    return {
        id: 'file_dropdown',
        Header: '',
        accessor: (row: Stoffstrombilanz) => row.id,
        disableSortBy: true,
        Cell: ({ value }: { value: string }) => {
            // Render custom component with formatting
            return value ? <ExportMenu stoffstrombilanzId={value} /> : '-';
        },
    };
};

function ExportMenu(props: { stoffstrombilanzId: string }) {
    const dispatch = useDispatch();

    if (!props.stoffstrombilanzId) return null;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <img
                src={csvDownloadIcon}
                style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                alt="csv"
                onClick={() => downloadFile('csv', props.stoffstrombilanzId, dispatch)}
            />
            <img
                src={pdfDownloadIcon}
                style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                alt="pdf"
                onClick={() => downloadFile('pdf', props.stoffstrombilanzId, dispatch)}
            />
            <img
                src={xlsDownloadIcon}
                style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                alt="xlsx"
                onClick={() => downloadFile('xlsx', props.stoffstrombilanzId, dispatch)}
            />
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
                style={{ color: '#ef4444', cursor: 'pointer' }}
                onClick={() => dispatch(deleteStoffstrombilanz(props.stoffstrombilanzId))}
            >
                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5.5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6zm2-.5a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5z" />
                <path
                    fillRule="evenodd"
                    d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1 0-2h3A1 1 0 0 1 6.5 1h3a1 1 0 0 1 1 1h3a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3a.5.5 0 0 0 0 1H13.5a.5.5 0 0 0 0-1H2.5z"
                />
            </svg>
        </div>
    );
}

const getEndDateColumn = () => {
    return {
        id: STOFFSTROMBILANZ_SORT_OPTIONS.END_DATE,
        Header: 'Enddatum',
        accessor: (row: Stoffstrombilanz) => {
            return {
                ...row,
            };
        },
        sortType: customDateSort, // Apply custom sorting function
        Cell: ({ value }: { value: Stoffstrombilanz | null }) => {
            // Render custom component with formatting
            return (
                <>
                    <StartEndDateColumn date={value?.endDate ? new Date(value.endDate) : undefined} />
                </>
            );
        },
    };
};

const StartEndDateColumn = (props: { date: Date | undefined }) => {
    if (props.date == null) return null;
    const day = props.date.getDate().toString().padStart(2, '0'); // Ensure day is padded to 2 digits
    const month = (props.date.getMonth() + 1).toString().padStart(2, '0'); // Ensure month is padded and adjust for 0-indexing
    const year = props.date.getFullYear();
    return (
        <>
            <span className={styles.dateText}>
                {day}.{month}.{year}
            </span>
        </>
    );
};

const DateElement = (props: { date: Date }) => {
    const day = props.date.getDate().toString().padStart(2, '0'); // Ensure day is padded to 2 digits
    const month = (props.date.getMonth() + 1).toString().padStart(2, '0'); // Ensure month is padded and adjust for 0-indexing
    const year = props.date.getFullYear();
    const hours = props.date.getHours().toString().padStart(2, '0'); // Ensure hours are padded
    const minutes = props.date.getMinutes().toString().padStart(2, '0'); // Ensure minutes are padded

    return (
        <>
            <div className={styles.columnText}>
                {day}.{month}.{year}
            </div>
            <div className={styles.columnSubtext}>
                {hours}:{minutes} Uhr
            </div>
        </>
    );
};

const getDownloadIcon = (fileType: string) => {
    switch (fileType) {
        case 'csv':
            return csvDownloadIcon;
        case 'pdf':
            return pdfDownloadIcon;
        case 'xlsx':
            return xlsDownloadIcon;
        default:
            return '';
    }
};

async function downloadFile(type: string, stoffstrombilanzId: string, dispatch: Dispatch) {
    try {
        const { apiUrl, isApp } = Config.getConfig();

        if (isApp()) {
            const downloadUrl = `${apiUrl}stoffstrombilanz/file/download?stoffstrombilanzId=${encodeURIComponent(
                stoffstrombilanzId
            )}&fileType=${encodeURIComponent(type)}`;

            FlutterBridge.sendDownloadLink({
                filename: `Stoffstrombilanz.${type}`,
                originalUrl: downloadUrl,
            });
            dispatch(downloadSuccess());
            return;
        }

        const response = (await postData(`stoffstrombilanz/file`, {
            stoffstrombilanzId: stoffstrombilanzId,
            fileType: type,
        })) as StoffstrombilanzFile;
        const byteCharacters = atob(response.base64Encoding);
        const mimeType = `application/${type}`;

        let length = byteCharacters.length;
        const out = new Uint8Array(length);

        while (length--) {
            out[length] = byteCharacters.charCodeAt(length);
        }

        const blob = new Blob([out], { type: mimeType });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Stoffstrombilanz.${type}`;
        link.click();
        URL.revokeObjectURL(link.href);
        dispatch(downloadSuccess());
    } catch (error) {
        dispatch(
            downloadError(
                'Die Datei konnte nicht heruntergeladen werden. Bitte überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut. Falls das Problem weiterhin besteht, wenden Sie sich bitte an den Support.'
            )
        );
        console.log(error);
    }
}

interface DownloadFileProps {
    base64: string | undefined;
    type: string;
    icon: string | undefined;
    fileName: string;
    stoffstrombilanzId?: string;
}

const DownloadFile = ({ base64, type, icon, fileName, stoffstrombilanzId }: DownloadFileProps) => {
    const isMobile = window.isMobile;
    const { apiUrl, isApp } = Config.getConfig();

    const handleDownload = () => {
        if (stoffstrombilanzId && isApp()) {
            const downloadUrl = `${apiUrl}stoffstrombilanz/file/download?stoffstrombilanzId=${encodeURIComponent(
                stoffstrombilanzId
            )}&fileType=${encodeURIComponent(type)}`;
            FlutterBridge.sendDownloadLink({
                filename: fileName ? `${fileName}.${type}` : `Stoffstrombilanz.${type}`,
                originalUrl: downloadUrl,
            });
            return;
        }
        if (!base64) return;
        const byteCharacters = atob(base64);
        const mimeType = `application/${type}`;

        let length = byteCharacters.length;
        const out = new Uint8Array(length);

        while (length--) {
            out[length] = byteCharacters.charCodeAt(length);
        }

        const blob = new Blob([out], { type: mimeType });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName ? `${fileName}.${type}` : `Stoffstrombilanz.${type}`;
        link.click();
        URL.revokeObjectURL(link.href);
    };
    return (
        <button
            className={'btn'}
            type="button"
            style={{ backgroundColor: '#FFD700', width: isMobile ? '30vw' : '30%' }}
            onClick={handleDownload}
        >
            <span>Download</span>
            <img src={icon} style={{ marginLeft: '4px', width: '20px', height: '20px' }} alt="pdf" />
        </button>
    );
};
